import React, { useState } from "react";
import { withNamespaces } from "react-i18next";
import { useHistory } from "react-router-dom";
import {
    Badge,
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Modal,
    Row,
    ModalHeader,
    ModalBody,
    ModalFooter,
    UncontrolledTooltip
} from "reactstrap";
import { INTERNATIONAL_DATE_FORMAT } from "src/common/constants";
import DateUtils from "src/services/utils/DateUtils";
import { APPROVAL_STATUS_MAP } from "src/modules/data-protection/constants";
import { useMutation } from "@tanstack/react-query";
import DataProtectionSoftwareService from "src/modules/data-protection/apis/DataProtectionSoftwareService";
import { toast } from "react-toastify";
import { connect } from "react-redux";

const APPROVE_ROLES = {
    "ai": "AI",
    "analyst": "Analyst",
    "dpo": "DPO"
}

const ApprovalConfirmationModal = ({
    t,
    isOpen,
    toggle,
    role,
    loading = false,
    onConfirm
}) => {
    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
            centered
        >
            <ModalHeader toggle={toggle}>
                {t('Confirm Compliance Approval')}
            </ModalHeader>

            <ModalBody>
                <p>
                    {t('You are about to approve this compliance review as {{role}}', {
                      role: ''  
                    })}{" "}
                    <strong>{t(APPROVE_ROLES[role])}</strong>.
                </p>

                <p>
                    {t('Your approval will be recorded in the audit trail and considered during the final compliance decision process.')}
                </p>

                <p className="mb-0">
                    {t('Please confirm that you have reviewed the associated risks, remediation tasks, and compliance recommendations before proceeding.')}
                </p>
            </ModalBody>

            <ModalFooter>
                <Button
                    color="light"
                    onClick={toggle}
                    disabled={loading}
                >
                    {t('Cancel')}
                </Button>

                <Button
                    color="success"
                    onClick={onConfirm}
                    disabled={loading}
                >
                    {loading && (
                        <span className="spinner-border spinner-border-sm me-2" />
                    )}

                    <i className="ri-check-line me-1" />
                    {t('Confirm Approval')}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

const ApprovalItem = ({
    t,
    title,
    status,
    approver,
    approvedAt,
    reason,
    canApprove,
    approvalBlocked,
    approvalBlockedTooltip,
    approvalBlockedTargetId,
    onGoToCeConformity,
    onApprove,
    onReject,
    isLoading
}) => {
    const dateUtils = new DateUtils();
    
    const config = APPROVAL_STATUS_MAP[status] || APPROVAL_STATUS_MAP.pending;

    return (
        <Card className="border shadow-none mb-3">
            <CardBody>
                <div className="d-flex justify-content-between align-items-start">
                    <div>
                        <div className="fw-semibold mb-2">
                            {t(title)}
                        </div>

                        <Badge 
                            color={config.color}
                            className={`bg-${config.color}`}
                        >
                            <i
                                className={`${config.icon} me-1`}
                            />
                            {t(config.label)}
                        </Badge>

                        {approver && (
                            <div className="text-muted small mt-2">
                                {t('Approved by {{name}}', {
                                    name: approver
                                })}
                            </div>
                        )}

                        {approvedAt && (
                            <div className="text-muted small">
                                {dateUtils.convertDateToDate(
                                    approvedAt,
                                    INTERNATIONAL_DATE_FORMAT
                                )}
                            </div>
                        )}

                        {reason && (
                            <div className="text-danger small mt-2">
                                <strong>{t('Reason')}:</strong>{" "} {reason}
                            </div>
                        )}
                    </div>

                    {canApprove &&
                        status === "pending" && (
                            <div className="d-flex gap-2">
                                <Button
                                    color="success"
                                    size="sm"
                                    onClick={onApprove}
                                    disabled={isLoading}
                                >
                                    <i className="ri-check-line me-1" />
                                    {t('Approve')}
                                </Button>

                                {/* <Button
                                    color="danger"
                                    size="sm"
                                    outline
                                    onClick={onReject}
                                >
                                    <i className="ri-close-line me-1" />
                                    {t('Reject')}
                                </Button> */}
                            </div>
                        )}

                    {approvalBlocked &&
                        status === "pending" && (
                            <div className="text-end">
                                <div id={approvalBlockedTargetId}>
                                    <Button
                                        color="success"
                                        size="sm"
                                        disabled
                                    >
                                        <i className="ri-check-line me-1" />
                                        {t('Approve')}
                                    </Button>

                                    <UncontrolledTooltip
                                        target={approvalBlockedTargetId}
                                    >
                                        {t(approvalBlockedTooltip)}
                                    </UncontrolledTooltip>
                                </div>
                            </div>
                        )}
                </div>
            </CardBody>
        </Card>
    );
};

const ComplianceApprovals = ({
    user,
    assetId,
    softwareDetailsQuery,
    t
}) => {
    const [ showApproveModalAs, setShowApproveModalAs ] = useState();
    const history = useHistory();
    
    const approveSoftwareMutation = useMutation({
        mutationFn: async ({ approveType }) => {
            const service = DataProtectionSoftwareService.getInstance();
            return await service.approve({
                softwareId: assetId,
                approvalType: approveType
            });
        },
        onSuccess: () => {
            toast(t("The compliance review has been approved successfully."), {
                type: "success"
            });

            softwareDetailsQuery.refetch();

            setShowApproveModalAs(null);
        },
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            if (error.response?.data?.error === 'error.responsible_person_required') {
                toast(t("A Responsible Person must be assigned before this software can be approved. Please update the Basic Information section and try again."), {
                    type: "error"
                });
            }
            else {
                toast(t("The approval could not be completed. Please try again."), {
                    type: "error"
                });
            }
        }
    });

    if (softwareDetailsQuery.isFetching) return null;

    const {
        approval_flow,
        analyst_assigned_object,
        provider_conformity_required,
        ce_readiness
    } = softwareDetailsQuery.data || {};

    const canApproveAsAnalyst = analyst_assigned_object?.id === user.id;
    const canApproveAsDpo = user.isDpo;
    const isCeApprovalBlocked = provider_conformity_required === true && ce_readiness === false;
    const aiReviewPending = !approval_flow?.ai?.approved;
    const canApproveAsAi = aiReviewPending
        && !isCeApprovalBlocked
        && (approval_flow?.ai?.can_approve ?? false);

    const goToCeConformityModule = () => {
        history.push({
            search: "?tab=compliance#ce-conformity"
        });
    };

    return (
        <Card className="mb-4">
            <CardHeader>
                <div className="d-flex align-items-center">
                    <i className="ri-user-star-line me-2 fs-5" />

                    <h5 className="mb-0">
                        {t('Compliance Approvals')}
                    </h5>
                </div>
            </CardHeader>

            <CardBody>
                <Row>
                    <Col lg={4}>
                        <ApprovalItem
                            t={t}
                            title="AI Review"
                            status={approval_flow?.ai?.approved ? 'approved' : 'pending'}
                            approver={approval_flow?.ai?.approved_by?.full_name}
                            approvedAt={approval_flow?.ai?.approved_at}
                            reason={''}
                            canApprove={canApproveAsAi}
                            approvalBlocked={aiReviewPending && isCeApprovalBlocked}
                            approvalBlockedTooltip="Complete CE Conformity requirements in the Compliance tab before AI approval can proceed."
                            approvalBlockedTargetId={`ai-review-approval-blocked-${assetId}`}
                            onGoToCeConformity={
                                isCeApprovalBlocked ? goToCeConformityModule : undefined
                            }
                            onApprove={() => setShowApproveModalAs('ai')}
                            isLoading={approveSoftwareMutation.isLoading}
                        />
                    </Col>

                    <Col lg={4}>
                        <ApprovalItem
                            t={t}
                            title="Analyst Review"
                            status={approval_flow?.analyst?.approved ? 'approved' : 'pending'}
                            approver={approval_flow?.analyst?.approved_by?.full_name}
                            approvedAt={approval_flow?.analyst?.approved_at}
                            reason={''}
                            canApprove={
                                canApproveAsAnalyst
                            }
                            onApprove={() => setShowApproveModalAs('analyst')}
                            onReject={() => console.log('reject')}
                            isLoading={approveSoftwareMutation.isLoading}
                        />
                    </Col>

                    <Col lg={4}>
                        <ApprovalItem
                            t={t}
                            title="DPO Review"
                            status={approval_flow?.dpo?.approved ? 'approved' : 'pending'}
                            approver={approval_flow?.dpo?.approved_by?.full_name}
                            approvedAt={approval_flow?.dpo?.approved_at}
                            reason={''}
                            canApprove={
                                canApproveAsDpo
                            }
                            onApprove={() => setShowApproveModalAs('dpo')}
                            onReject={() => console.log('ok')}
                            isLoading={approveSoftwareMutation.isLoading}
                        />
                    </Col>
                </Row>
            </CardBody>

            <ApprovalConfirmationModal 
                t={t}
                isOpen={!!showApproveModalAs}
                toggle={() => setShowApproveModalAs(null)}
                onConfirm={() => approveSoftwareMutation.mutate({
                    approveType: showApproveModalAs
                })}
                role={showApproveModalAs}
                loading={approveSoftwareMutation.isLoading}
            />
        </Card>
    );
};

const mapStatetoProps = (state) => {
    const { user } = state.Login;

    return {
        user
    };
};

export default withNamespaces()(
    connect(
        mapStatetoProps
    )(ComplianceApprovals)
);